/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';

import type {
	Command,
	FloatingToolbarButton as FloatingToolbarButtonDescriptor,
} from '@atlaskit/editor-common/types';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
import { token } from '@atlaskit/tokens';
import { ConversationAssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components/ui/ConversationAssistantButton';

/** Props for the Ask Rovo toolbar button. */
export type AskRovoToolbarButtonProps = {
	button: FloatingToolbarButtonDescriptor<Command>;
	isDisabled?: boolean;
	onClick: () => void;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
const buttonWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Remove TopNavButton ::after border
	'& button': { '&::after': { display: 'none' } },
});

const buttonOverlaySurfaceStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Match the TopNavButton surface to its floating-toolbar container
	'& button': {
		backgroundColor: token('elevation.surface.overlay'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Match the neighboring floating-toolbar button hover state
	'& button:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

const buttonLabelStyles = css({
	color: token('color.text.subtle'),
});

const buttonSpacingStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- override ConversationAssistantButtonNav4 padding/gap to match the neighbouring toolbar button
	'& button': {
		paddingInline: token('space.100'),
		gap: token('space.075'),
	},
});

/**
 * Renders the Ask Rovo button for the native embed floating toolbar.
 */
export function AskRovoToolbarButton({
	button,
	isDisabled,
	onClick,
}: AskRovoToolbarButtonProps): React.JSX.Element {
	const tooltipContent =
		typeof button.tooltipContent !== 'function' ? button.tooltipContent : undefined;
	return (
		<div
			css={[
				buttonWrapperStyles,
				buttonOverlaySurfaceStyles,
				expVal('cc_maui_polish_changes_batch_3', 'isEnabled', false) && buttonSpacingStyles,
			]}
		>
			<ConversationAssistantButtonNav4
				isActive={false}
				isDisabled={isDisabled}
				label={<span css={buttonLabelStyles}>{button.title}</span>}
				onClick={onClick}
				productKey="confluence"
				siteId="stub-site-id"
				tooltipContent={tooltipContent}
			/>
		</div>
	);
}
